import {
  areAllSelected,
  getFilteredCategories,
  sortCategories,
} from "./CategoriesFilter.utils";

interface RootCategory {
  rootKey: string;
}

interface CategoriesFilterProps {
  categories: RootCategory[] | undefined;
  selectedCategories: string[];
  isSaving: boolean;
  onToggleCategory: (rootKey: string) => Promise<void>;
  onToggleAllCategories: () => Promise<void>;
}

export default function CategoriesFilter({
  categories,
  selectedCategories,
  isSaving,
  onToggleCategory,
  onToggleAllCategories,
}: CategoriesFilterProps) {
  // Helper functions for root categories
  const filteredCategories = sortCategories(
    getFilteredCategories(categories)
  );

  if (!categories) return null;

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base sm:text-lg font-medium text-gray-900">
          Categories ({selectedCategories.length}/{categories?.length || 0})
        </h3>
        <button
          onPointerDown={onToggleAllCategories}
          disabled={isSaving}
          className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
        >
          {areAllSelected(filteredCategories, selectedCategories)
            ? "Deselect All"
            : "Select All"}
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {(() => {
          // Get filtered root categories
          if (filteredCategories.length === 0) {
            return (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500">No categories available</p>
              </div>
            );
          }

          return filteredCategories.map((category) => {
            const isSelected = selectedCategories.includes(category.rootKey);
            return (
              <label
                key={category.rootKey}
                className={`
                  flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors
                  ${
                    isSelected
                      ? "border-purple-500 bg-purple-50 shadow-sm"
                      : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
                  }
                  ${isSaving ? "opacity-50 cursor-not-allowed" : ""}
                `}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleCategory(category.rootKey)}
                  disabled={isSaving}
                  className="w-4 h-4 text-purple-600 bg-white border-purple-300 rounded focus:ring-purple-500 focus:ring-2 accent-purple-600 flex-shrink-0"
                />
                <span
                  className={`text-xs sm:text-sm font-medium truncate ${
                    isSelected ? "text-purple-900" : "text-gray-900"
                  }`}
                  title={category.rootKey}
                >
                  {category.rootKey}
                </span>
              </label>
            );
          });
        })()}
      </div>
    </div>
  );
}
